// Dev/acceptance-only floating control for the assign-to-reviewer demo.
// Lets a single browser switch between reviewer perspectives so the demo can
// show "exam disappears from my queue → appears in their queue" in one session.

import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { isDevEnvironment } from "$routes/_lib/environment";
import { Box, MenuItem, Select, styled, Typography } from "@material-ui/core";
import {
  EPOCUS_BORDER,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "./epocus-palette";
import { REVIEWERS } from "./reviewers";
import {
  ALL_VIEWERS,
  useDemoViewer,
  useSetDemoViewer,
} from "./use-demo-viewer";

const FloatingPanel = styled(Box)({
  position: "fixed",
  bottom: 80,
  right: 16,
  zIndex: 1300,
  backgroundColor: EPOCUS_WHITE,
  border: `1px solid ${EPOCUS_BORDER}`,
  borderRadius: 12,
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  padding: "8px 12px",
  display: "flex",
  flexDirection: "column",
  gap: 4,
  minWidth: 220,
});

const Label = styled(Typography)({
  fontSize: rem(10),
  fontWeight: 700,
  color: EPOCUS_TEXT_SECONDARY,
  textTransform: "uppercase",
  letterSpacing: 0.5,
});

const ViewerName = styled("span")({
  fontSize: rem(13),
  fontWeight: 600,
  color: EPOCUS_TEXT_PRIMARY,
});

export const EpocusDemoViewerSwitcher: FC = () => {
  const [assignToReviewerEnabled] = useFeatureFlag(
    featureFlags.epocus.assignToReviewer,
  );

  // Keep the demo localStorage hooks out of the flag-off path entirely — they
  // only subscribe once the gate below has passed.
  if (!isDevEnvironment() || !assignToReviewerEnabled) return null;

  return <DemoViewerSwitcherPanel />;
};

const DemoViewerSwitcherPanel: FC = () => {
  const viewerId = useDemoViewer();
  const setViewer = useSetDemoViewer();

  return (
    <FloatingPanel role="region" aria-label="Demo viewer switcher">
      <Label>Demo viewer</Label>
      <Select
        value={viewerId}
        onChange={(e) => setViewer(String(e.target.value))}
        disableUnderline
        style={{ fontSize: rem(16) }}
        inputProps={{ "aria-label": "Current demo viewer" }}
      >
        <MenuItem value={ALL_VIEWERS}>
          <ViewerName>All reviewers (admin view)</ViewerName>
        </MenuItem>
        {REVIEWERS.map((r) => (
          <MenuItem key={r.id} value={r.id}>
            <Box display="flex" flexDirection="column">
              <ViewerName>{r.name}</ViewerName>
              <Typography
                style={{ fontSize: rem(11), color: EPOCUS_TEXT_SECONDARY }}
              >
                {r.role}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FloatingPanel>
  );
};
